import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSupplies } from '../../hooks/useSupplies';
import { fetchSupplies } from '../../features/supplies/suppliesSlice';
import { fetchGroupSupplie } from '../../features/groups/groupsSlice';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import {
  Checkbox,
  DialogContent,
  DialogTitle,
  FormGroup,
  IconButton,
  InputBase,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ModalButton, ModalDialogActions } from '../ModalComponents';

import styles from '../ModalComponent/modalStyles.module.css';

export default function ModalAddSupplyGroup({ id, fetchData, selectedSuppliesList }) {
  const [open, setOpen] = useState(false);
  const [errorResponse, setErrorResponse] = useState('');
  const [suppliesList, setSuppliesList] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [filteredSuppliesList, setFilteredSuppliesList] = useState(null);

  const { t } = useTranslation();
  const { supplies, isLoading } = useSupplies();
  const dispatch = useDispatch();

  const isDirty = selectedSuppliers.length > 0;
  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = async () => {
    setOpen(true);
    if (!supplies || supplies.length === 0) {
      await dispatch(fetchSupplies());
    }
  };
  const doClose = () => {
    setOpen(false);
    setSelectedSuppliers([]);
    setSuppliesList(null);
    setFilteredSuppliesList(null);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (supplies && open) {
      const filteredSupplies = supplies.filter(
        (supply) => !selectedSuppliesList.includes(supply.id)
      );
      setSuppliesList(filteredSupplies);
      setFilteredSuppliesList(filteredSupplies);
    }
  }, [supplies, open, selectedSuppliesList]);

  const handleSupplierToggle = (supplierId) => () => {
    const currentIndex = selectedSuppliers.indexOf(supplierId);
    const newSelectedSuppliers = [...selectedSuppliers];

    if (currentIndex === -1) {
      newSelectedSuppliers.push(supplierId);
    } else {
      newSelectedSuppliers.splice(currentIndex, 1);
    }

    setSelectedSuppliers(newSelectedSuppliers);
  };

  const handleSubmit = async () => {
    try {
      const selectedSuppliersNumbers = selectedSuppliers.map((supplier) => parseInt(supplier));
      const formData = {
        machineGroup: { id },
        machineSupplies: selectedSuppliersNumbers,
      };
      await dispatch(fetchGroupSupplie(formData));
      await fetchData();
      doClose();
    } catch (err) {
      setErrorResponse(err);
      console.error('Error add document:', err);
    }
  };

  const requestSearch = (searchedVal) => {
    if (!filteredSuppliesList) return;

    const filteredRows = filteredSuppliesList.filter((item) => {
      const lowerCaseSearchedVal = searchedVal.toLowerCase();
      return (
        item.name.toLowerCase().includes(lowerCaseSearchedVal) ||
        item.description.toLowerCase().includes(lowerCaseSearchedVal)
      );
    });
    setSuppliesList(filteredRows);
  };

  return (
    <div>
      <ModalButton
        variant="contained"
        color="primary"
        onClick={handleOpen}
        style={{ padding: '12px 24px', textTransform: 'none' }}
        startIcon={<AddIcon />}
      >
        {t('modals.add')}
      </ModalButton>
      <ModalComponent open={open} handleClose={handleClose}>
        <Box
          className={styles.modal}
          sx={{
            height: '75vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2, flexShrink: 0 }}>{t('modals.addSupply')}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent
            dividers
            sx={{
              p: 3,
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '60vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'var(--scrollbar-track)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--scrollbar-thumb)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'var(--scrollbar-thumb-hover)',
              },
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
              <Paper
                component="form"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  border: '1px solid var(--table-border)',
                  borderRadius: '12px',
                  backgroundColor: 'var(--table-row-white)',
                  boxShadow: 'none',
                  flexShrink: 0,
                  '&:hover': {
                    border: '1px solid var(--table-border)',
                  },
                  '&:focus-within': {
                    border: '1px solid #3b82f6',
                    boxShadow: '0 0 0 3px rgb(59 130 246 / 0.1)',
                  },
                }}
              >
                <IconButton
                  type="button"
                  sx={{
                    p: '12px',
                    color: 'var(--text-secondary)',
                  }}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
                <InputBase
                  sx={{
                    ml: 1,
                    flex: 1,
                    fontSize: '0.875rem',
                    '& input': {
                      padding: '12px 16px 12px 0',
                      color: 'var(--text-primary)',
                    },
                  }}
                  placeholder={t('generic.search')}
                  onChange={(e) => {
                    requestSearch(e.target.value);
                  }}
                />
              </Paper>

              <FormGroup sx={{ flex: 1, overflowY: 'auto', flexWrap: 'nowrap' }}>
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px',
                    }}
                  >
                    <Typography variant="body1">{t('generic.loadingData')}</Typography>
                  </div>
                ) : suppliesList && suppliesList.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead
                      style={{
                        marginBottom: '10px',
                        height: '30px',
                        position: 'sticky',
                        top: '0',
                        zIndex: '1',
                        backgroundColor: 'var(--table-row-white)',
                      }}
                    >
                      <tr>
                        <th></th>
                        <th style={{ textAlign: 'left' }}>{t('generic.number')}</th>
                        <th style={{ textAlign: 'left' }}>{t('generic.name')}</th>
                        <th style={{ textAlign: 'left' }}>{t('generic.description')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliesList.map((supplier) => (
                        <tr
                          key={supplier.id}
                          style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--table-border)',
                          }}
                          onClick={handleSupplierToggle(supplier.id)}
                        >
                          <td
                            style={{ padding: '8px', width: '40px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedSuppliers.includes(supplier.id)}
                              onChange={handleSupplierToggle(supplier.id)}
                              color="primary"
                              size="small"
                            />
                          </td>
                          <td style={{ padding: '8px' }}>{supplier.number}</td>
                          <td style={{ padding: '8px' }}>{supplier.name}</td>
                          <td style={{ padding: '8px', maxWidth: '150px' }}>
                            {supplier.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Typography variant="body1">{t('modals.noSupplies')}</Typography>
                )}
              </FormGroup>

              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                isLoading ||
                (suppliesList && suppliesList.length === 0) ||
                selectedSuppliers.length === 0
              }
            >
              {t('modals.add')}
            </ModalButton>
          </ModalDialogActions>
        </Box>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
